/**
 * A KAMERA — teljes képernyős MŰVELET, nem oldal.
 *
 * A `CLAUDE.md` §2 kemény szabálya: negyedik felület nincs, és a fotózás nem
 * nyit új tengelyt. Ezért ez egy réteg a hiánylista fölött, nem útvonal —
 * bezárva pontosan oda tér vissza, ahonnan nyílt.
 *
 * ── AMI A FEJLÉCBEN VAN, ÉS MIÉRT ───────────────────────────────────────
 *
 * A kért számla neve és összege VÉGIG látszik. Nem dísz: ettől nem kerül a
 * fotó rossz bizonylathoz. Aki három számlát fotóz egymás után, a harmadiknál
 * már nem emlékszik, melyiket kezdte el.
 *
 * ── KÉZI EXPONÁLÁS, UTÁNA VÁGÁS (#89) ───────────────────────────────────
 *
 * 2026-11-02-ig élő lapfelismerés és automata exponálás volt itt. A telefonos
 * próbán a felismerő a lap szélét nem találta (fehér számla világos
 * asztalon), előtte a tételtáblát vágta ki (#88). Viktor döntése: kézi
 * exponálás, és MINDEN fotó után a vágó nézet (`CropView`), négy húzható
 * sarokkal. A felismerő csak a sarkok KEZDŐHELYÉT javasolja, az álló, teljes
 * felbontású képen — a döntés az ügyfélé, aki látja, mit vág le.
 *
 * Az eredeti fotó a lappal együtt a telefonon marad a küldésig, így az
 * áttekintőről a vágás újra igazítható („Vágás"). A szerverre a kivágott
 * lap megy.
 *
 * **Vaku nincs**: iOS Safariban a `torch` nem kapcsolható
 * (`szkenneles-terv.md` §6). Egy vezérlő, ami a felhasználók felén nem
 * csinál semmit, rosszabb, mint a hiánya.
 */

import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@/i18n/generated';
import { CropView } from './CropView';
import { fullFrameCorners, isFullFrame, suggestedCorners } from './crop-geometry';
import {
  type CapturedPage,
  createScanicScanner,
  type DocumentScanner,
  type QuadCorners,
} from './document-scanner';
import { ReviewSheet } from './ReviewSheet';
import { canAddPage } from './session-limits';
import { useCameraStream } from './useCameraStream';
import { type CaptureSession, useCaptureSession } from './useCaptureSession';
import { type PendingSession, type Retention, useRetention } from './useRetention';

/**
 * A JAVASLAT FELISMERÉSÉNEK KICSINYÍTÉSE. A `scanic` a kisebb képen gyorsabb,
 * és a sarkok helyét úgyis az ügyfél igazítja — a javaslatnak elég a közelítés.
 */
const DETECTION_WIDTH = 640;

interface CameraSheetProps {
  /**
   * MELYIK KÉRÉSHEZ TARTOZIK A SOROZAT — a megőrzés kulcsa (Sz6).
   *
   * `null` esetén nincs megőrzés.
   */
  storageKey: string | null;
  /** Mit kértek — a fejléc ezt mutatja végig. */
  title: string;
  subtitle?: string;
  /** Bezárás mentés nélkül. */
  onCancel: () => void;
  /** A kész sorozat, a küldés sorrendjében — az áttekintőn jóváhagyva. */
  onDone: (pages: Blob[]) => void;
}

/** A VÁGÓ NÉZET BEMENETE — friss fotó vagy az áttekintőről újravágott lap. */
interface CropTarget {
  /** Az eredeti fotó URL-je a nézetnek; bezáráskor visszavonjuk. */
  src: string;
  /** A képkocka a kivágáshoz — csak amíg a nézet nyitva van. */
  frame: ImageData;
  original: Blob;
  corners: QuadCorners;
  /** Melyik lapot cseréli; `null` = új lap a sorozat végére. */
  replaceId: string | null;
  /** Az áttekintőről nyílt-e — „Mégsem"/„Kész" után oda térünk vissza. */
  fromReview: boolean;
  /** A lap sorszáma a fejlécben. */
  number: number;
}

export function CameraSheet({ storageKey, title, subtitle, onCancel, onDone }: CameraSheetProps) {
  const { t } = useTranslation();
  const camera = useCameraStream(true);
  const session = useCaptureSession();
  const retention = useRetention(storageKey, session.pages, session.adopt);

  /**
   * AZ EXPONÁLÁS VILLANÁSA (#87) — számláló, nem kapcsoló: minden exponálás
   * ÚJ elemet tesz a képbe (`key`), így a CSS-animáció minden alkalommal
   * elölről indul, akkor is, ha két fotó gyorsan követi egymást.
   */
  const [flashes, setFlashes] = useState(0);

  // A FOTÓZÁS RÉSZEI EGY KOMPONENSBEN. Az áttekintő és a vágó nézet RÉTEG a
  // kamera fölött, nem külön nézet: a videó mögöttük tovább él, tehát a „Még
  // fotózok" nem indít új `getUserMedia`-t. iOS-en az újraindítás
  // másodperceket kér, és újra engedélyt is kérhet.
  const [reviewing, setReviewing] = useState(false);
  const [crop, setCrop] = useState<CropTarget | null>(null);
  const [cropBusy, setCropBusy] = useState(false);

  // Az áttekintőről indított ÚJRAFOTÓZÁS célja — a következő fotó ezt a lapot
  // cseréli, a HELYÉN, nem a sorozat végére kerül.
  const retakeRef = useRef<string | null>(null);

  /**
   * AMI AZ EXPONÁLÁS UTÁN KÖZLENDŐ — korlát vagy bukott fotó.
   *
   * Egy sor, nem három: mindegyik ugyanarra a kérdésre válaszol („miért
   * nem lett lap?"), és a kamerában nincs hely több üzenethelynek.
   */
  const [notice, setNotice] = useState<string | null>(null);

  // A SCANNER PÉLDÁNY A KOMPONENS ÉLETTARTAMÁRA. A `scanic` a WASM-példányt
  // életben tartja; a felismerő a kamerával párhuzamosan töltődik (#87).
  const scannerRef = useRef<DocumentScanner | null>(null);
  if (!scannerRef.current) {
    scannerRef.current = createScanicScanner();
    scannerRef.current.warmUp?.();
  }

  const detectionCanvas = useRef<HTMLCanvasElement | null>(null);
  const captureCanvas = useRef<HTMLCanvasElement | null>(null);

  // ⚠️ AZ EXPONÁLÁS ÚJRAHÍVHATÓSÁGA ELLEN. A javaslat felismerése és az
  // eredeti fotó kódolása nem pillanat; egy második koppintás közben egy
  // második vágó nézetet nyitna az elsőre.
  const capturingRef = useRef(false);

  /** A KÉSZ LAP FELVÉTELE — a méretkorlát itt mérhető először. */
  const absorb = useCallback(
    (page: CapturedPage) => {
      const blobs = session.pages.map((current) => current.blob);
      // A MÉRET CSAK MOST MÉRHETŐ. Ha nem fér bele, a lapot NEM vesszük fel —
      // de meg is mondjuk, miért.
      if (canAddPage(blobs, page.blob.size) === 'too-large') {
        setNotice('capture.limit.tooLarge');
        return;
      }
      session.add(page);
      setNotice(null);
    },
    [session]
  );

  const capture = useCallback(async () => {
    const video = camera.videoRef.current;
    const scanner = scannerRef.current;
    // ⚠️ A `retention.pending` alatt nincs exponálás: amíg a félbehagyott
    // sorozatról nem döntött, egy most készült lap a döntés után vagy
    // eltűnne, vagy egy olyan sorozatban maradna, amiről azt hiszi, üres.
    // ⚠️ NYITOTT VÁGÓ NÉZET ALATT SINCS: a kioldó a nézet mögött ugyan nem
    // látszik, de egy második fotó az elsőt ELDOBNÁ a vágás közepén.
    if (
      !(video && scanner) ||
      video.readyState < 2 ||
      capturingRef.current ||
      retention.pending ||
      crop !== null
    ) {
      return;
    }

    // A DARABSZÁM AZ EXPONÁLÁS ELŐTT DŐL EL. ⚠️ Az újrafotózás kivétel: az
    // nem növeli a sorozatot.
    const blobs = session.pages.map((page) => page.blob);
    if (!retakeRef.current && canAddPage(blobs, 0) === 'too-many-pages') {
      setNotice('capture.limit.tooManyPages');
      return;
    }

    captureCanvas.current ??= document.createElement('canvas');
    const frame = grabFrame(video, captureCanvas.current);
    if (!frame) {
      return;
    }

    // A VILLANÁS A KÉPKOCKA PILLANATÁBAN (#87): az ügyfél ekkor tudja, hogy
    // a fotó elkészült.
    setFlashes((count) => count + 1);
    navigator.vibrate?.(30);

    capturingRef.current = true;
    try {
      const detected = await suggestOnStill(video, scanner, detectionCanvas);
      const original = await scanner.encode(frame);
      const replaceId = retakeRef.current;
      const number = replaceId
        ? session.pages.findIndex((page) => page.id === replaceId) + 1
        : session.pages.length + 1;
      setCrop({
        src: URL.createObjectURL(original),
        frame,
        original,
        corners: suggestedCorners(detected, frame.width, frame.height),
        replaceId,
        fromReview: false,
        number,
      });
    } catch {
      // A KÓDOLÁS DOBHAT (nincs 2d környezet, nem jött JPEG). Enélkül a
      // koppintásra nem történne semmi — se lap, se üzenet.
      setNotice('capture.failed');
    } finally {
      capturingRef.current = false;
    }
  }, [camera.videoRef, session, retention.pending, crop]);

  /**
   * A vágó nézet bezárása — az URL-t visszavonjuk, a képkocka elengedhető.
   *
   * HOVA TÉRÜNK VISSZA: az áttekintőről nyílt vágás oda; egy újrafotózott lap
   * KÉSZ vágása is oda (onnan indult). Az „Újrafotózom" viszont a kamerához
   * visz — a függő újrafotózás megmarad, a következő fotó ugyanazt cseréli.
   */
  const closeCrop = useCallback((target: CropTarget, finished: boolean) => {
    URL.revokeObjectURL(target.src);
    setCrop(null);
    if (target.fromReview || (finished && target.replaceId)) {
      setReviewing(true);
    }
  }, []);

  const finishCrop = useCallback(
    async (target: CropTarget, corners: QuadCorners) => {
      const scanner = scannerRef.current;
      if (!scanner) {
        return;
      }
      setCropBusy(true);
      try {
        const { width, height } = target.frame;
        // A TELJES KÉP NEM VÁGÁS: a fotó úgy megy, ahogy van — és ez az
        // ügyfél döntése, tehát a lap széle „biztos" (`cropped: true`).
        const full = isFullFrame(corners, width, height);
        const result = await scanner.capture(target.frame, full ? null : corners);
        const page: CapturedPage = {
          blob: result.blob,
          cropped: full ? true : result.cropped,
          original: target.original,
          corners,
        };

        if (target.replaceId) {
          retakeRef.current = null;
          session.replace(target.replaceId, page);
        } else {
          absorb(page);
        }
      } catch {
        setNotice('capture.failed');
      } finally {
        setCropBusy(false);
        closeCrop(target, true);
      }
    },
    [session, absorb, closeCrop]
  );

  /** „Vágás" az áttekintőről — az eredeti fotóból, a korábbi sarkokkal. */
  const recrop = useCallback(
    async (id: string) => {
      const scanner = scannerRef.current;
      const index = session.pages.findIndex((page) => page.id === id);
      const page = session.pages[index];
      // ⚠️ ÚJRAHÍVÁS ELLEN, mint az exponálásnál: a visszaolvasás alatt az
      // áttekintő nyitva van, és egy dupla koppintás két vágó nézetet nyitna —
      // az első URL-je visszavonatlanul maradna.
      if (!(scanner && page?.original) || capturingRef.current) {
        return;
      }
      capturingRef.current = true;
      try {
        const frame = await scanner.decode(page.original);
        setReviewing(false);
        setCrop({
          src: URL.createObjectURL(page.original),
          frame,
          original: page.original,
          corners: page.corners ?? fullFrameCorners(frame.width, frame.height),
          replaceId: id,
          fromReview: true,
          number: index + 1,
        });
      } catch {
        setNotice('capture.failed');
      } finally {
        capturingRef.current = false;
      }
    },
    [session.pages]
  );

  if (camera.error) {
    return (
      <CameraError messageKey={camera.error} onRetry={camera.retry} onCancel={onCancel} t={t} />
    );
  }

  const pageCount = session.pages.length;

  return (
    <div className="camera" role="dialog" aria-modal="true" aria-label={t('capture.title')}>
      <div className="camera-view">
        {/* `playsInline` NÉLKÜL az iOS Safari TELJES KÉPERNYŐRE nyitja a
            videót, saját vezérlőkkel — és a mi felületünk eltűnik mögüle. */}
        <video ref={camera.videoRef} autoPlay playsInline muted />

        {flashes > 0 ? <span key={flashes} className="camera-flash" aria-hidden="true" /> : null}

        {/* NINCS ÉLŐ KERET (#89): a felismerő a valós körülmények közt nem
            találta a lapot, és egy soha zöldre nem váltó keret csak
            bizonytalanít. A teendő egy mondat. */}
        <p className="camera-hint">{t('capture.aimWhole')}</p>

        <header className="camera-head">
          <button
            type="button"
            className="camera-close"
            onClick={() => {
              retention.forget();
              onCancel();
            }}
          >
            {t('capture.cancel')}
          </button>
          <span className="camera-target">
            <b>{title}</b>
            {subtitle ? <span className="num">{subtitle}</span> : null}
          </span>
        </header>
      </div>

      <div className="camera-dock">
        {retention.pending ? (
          <ResumeOffer pending={retention.pending} retention={retention} t={t} />
        ) : null}

        {notice ? (
          <p className="camera-notice" role="alert">
            {t(notice)}
          </p>
        ) : null}

        <div className="camera-strip">
          {pageCount === 0 ? (
            <span className="camera-strip-empty">{t('capture.noPagesYet')}</span>
          ) : (
            session.pages.map((page, index) => (
              <span className="camera-thumb" key={page.id}>
                <img src={page.previewUrl} alt={t('capture.pageNumber', { number: index + 1 })} />
                <b className="num">{index + 1}</b>
              </span>
            ))
          )}
        </div>

        <div className="camera-controls">
          <span className="camera-controls-spacer" aria-hidden="true" />

          <button
            type="button"
            className="camera-shutter"
            aria-label={t('capture.shutter')}
            disabled={retention.pending !== null}
            onClick={() => void capture()}
          >
            <span />
          </button>

          <button
            type="button"
            className="camera-done"
            disabled={pageCount === 0}
            onClick={() => setReviewing(true)}
          >
            {pageCount === 0 ? t('capture.done') : t('capture.doneWithCount', { count: pageCount })}
          </button>
        </div>
      </div>

      {reviewing ? (
        <ReviewSheet
          pages={session.pages}
          onReorder={session.reorder}
          onRemove={session.remove}
          onRetake={(id) => {
            retakeRef.current = id;
            setReviewing(false);
          }}
          onCrop={(id) => void recrop(id)}
          onBack={() => {
            // A „MÉG FOTÓZOK" ELEJTI A FÜGGŐ ÚJRAFOTÓZÁST: a következő fotó új lap.
            retakeRef.current = null;
            setReviewing(false);
          }}
          onSend={() => {
            retention.forget();
            onDone(session.pages.map((page) => page.blob));
          }}
        />
      ) : null}

      {crop ? (
        <CropView
          key={crop.src}
          src={crop.src}
          width={crop.frame.width}
          height={crop.frame.height}
          initial={crop.corners}
          title={t('capture.crop.title', { number: crop.number })}
          backLabel={t(crop.fromReview ? 'capture.cancel' : 'capture.review.retake')}
          onBack={() => closeCrop(crop, false)}
          onDone={(corners) => void finishCrop(crop, corners)}
          busy={cropBusy}
        />
      ) : null}
    </div>
  );
}

/**
 * A SARKOK JAVASLATA AZ ÁLLÓ KÉPEN — kicsinyítve felismerve, majd a teljes
 * felbontásra méretezve. A felismerés bukása nem hiba: a javaslat akkor a
 * kép beljebb húzott széle lesz (`suggestedCorners`).
 */
async function suggestOnStill(
  video: HTMLVideoElement,
  scanner: DocumentScanner,
  canvas: { current: HTMLCanvasElement | null }
): Promise<QuadCorners | null> {
  try {
    canvas.current ??= document.createElement('canvas');
    const small = grabFrame(video, canvas.current, DETECTION_WIDTH);
    if (!small) {
      return null;
    }
    const corners = await scanner.detect(small);
    return corners ? scaleCorners(corners, video.videoWidth / DETECTION_WIDTH) : null;
  } catch {
    return null;
  }
}

function ResumeOffer({
  pending,
  retention,
  t,
}: {
  pending: PendingSession;
  retention: Retention;
  /**
   * Behelyettesítő fordító. A `Translate` (`@/i18n/generated`) szándékosan
   * paraméter nélküli — itt viszont lapszám és időpont megy a mondatba.
   */
  t: (key: string, values?: Record<string, unknown>) => string;
}) {
  return (
    <div className="camera-resume" role="alert">
      <p>
        {t('capture.resume.question', {
          count: pending.pageCount,
          when: formatDateTime(new Date(pending.startedAt)),
        })}
      </p>
      <div className="camera-resume-actions">
        <button type="button" className="pick pick-primary" onClick={retention.resume}>
          {t('capture.resume.continue')}
        </button>
        <button type="button" className="pick pick-link" onClick={retention.discard}>
          {t('capture.resume.restart')}
        </button>
      </div>
    </div>
  );
}

/**
 * A HIBAKÉPERNYŐ. Minden ág MÁS teendőt ad — ezért van négy szöveg, nem egy
 * „nem sikerült" (`camera-errors.ts`).
 *
 * A „Fájlt választok" MINDEN ágon ott van, a kamerahiba fajtájától
 * függetlenül: az ügyfélnek a papír a kezében van, és a rendszer dolga, hogy
 * be tudja adni — nem az, hogy megmagyarázza, miért nem megy a kamera.
 */
function CameraError({
  messageKey,
  onRetry,
  onCancel,
  t,
}: {
  messageKey: string;
  onRetry: () => void;
  onCancel: () => void;
  t: (key: string) => string;
}) {
  return (
    <div
      className="camera camera-failed"
      role="dialog"
      aria-modal="true"
      aria-label={t('capture.error.title')}
    >
      <div className="card">
        <p className="eyebrow">{t('capture.error.title')}</p>
        <p>{t(`capture.error.${messageKey}`)}</p>
        <div className="request-actions">
          <button type="button" className="pick pick-primary" onClick={onRetry}>
            {t('capture.error.retry')}
          </button>
          {/* ⚠️ EZ A GOMB BEZÁR, NEM FÁJLT VÁLASZT — ezért „Vissza". A kérés
              kártyáján ott a valódi fájlválasztó, egy koppintásra innen; egy
              második út ugyanoda nem ér annyit, mint egy őszinte felirat. A
              hibaüzenet mondja meg, mit tegyen ott. */}
          <button type="button" className="pick pick-link" onClick={onCancel}>
            {t('capture.error.back')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * EGY KÉPKOCKA A VIDEÓBÓL, `ImageData`-ként.
 *
 * A `width` megadásával kicsinyítve (keresőkép), nélküle teljes
 * felbontásban (exponálás). Ugyanaz a kód volt kétszer, két elgépelési
 * lehetőséggel — a `willReadFrequently` különösen: nélküle a böngésző a
 * canvast GPU-ra teszi, és minden `getImageData` visszamásolja a memóriába.
 * Képkockánként nyolcszor ez mérhető akadás.
 *
 * `null`, ha nincs 2d környezet. Nem dobunk: a hívó ilyenkor egyszerűen
 * kihagyja ezt a képkockát, és a következővel próbálja.
 */
function grabFrame(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  width?: number
): ImageData | null {
  const targetWidth = width ?? video.videoWidth;
  canvas.width = targetWidth;
  canvas.height = Math.round(targetWidth * (video.videoHeight / video.videoWidth));

  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    return null;
  }
  context.drawImage(video, 0, 0, canvas.width, canvas.height);
  return context.getImageData(0, 0, canvas.width, canvas.height);
}

/**
 * A SARKOK ÁTMÉRETEZÉSE a detektálás felbontásáról a teljesre.
 *
 * ⚠️ EZ NEM ELHAGYHATÓ. A `scanic` azon a képen dolgozik, amit kap — nálunk
 * a 640 képpont széles keresőképen —, a kivágás viszont a teljes felbontású
 * képkockán történik. Skálázás nélkül a kivágás a lap bal felső TÖREDÉKÉT
 * venné, és a hiba „működőnek" látszana: keletkezne kép, csak épp rossz.
 */
function scaleCorners(corners: QuadCorners, scale: number): QuadCorners {
  const at = (point: { x: number; y: number }) => ({ x: point.x * scale, y: point.y * scale });
  return {
    topLeft: at(corners.topLeft),
    topRight: at(corners.topRight),
    bottomRight: at(corners.bottomRight),
    bottomLeft: at(corners.bottomLeft),
  };
}

export type { CaptureSession };
